import { forwardRef } from 'react';
import type { PropsWithoutRef, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';
import { hideVisually, rgba } from 'polished';

import type { BaseProps, ForwardRefForwardPropsComponent } from '../../types';
import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils';
import { calculateFontSize, readableColor } from '../../styles';
import type { FontSize } from '../../styles';

export interface LabelProps extends BaseProps {
  /**
   * Id for the label.
   * @default undefined
   */
  id?: string;
  /**
   * HTML tag to render the label as.
   * @default 'label'
   */
  as?: 'label' | 'div' | 'span';
  /** Use to associate with a form-element's id. */
  htmlFor?: string;
  /**
   * Visually hides the label.
   * @default false
   */
  labelHidden?: boolean;
  /** Render the label inline. */
  inline?: boolean;
  /** Content/text for the label. */
  children: ReactNode;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLLabelElement | HTMLLegendElement | HTMLDivElement | HTMLSpanElement>;
}

export const StyledLabel = styled.label<Pick<LabelProps, 'labelHidden' | 'htmlFor' | 'inline'>>(
  ({ theme, labelHidden, inline }) => {
    const { 'foreground-color': labelForegroundColor } = theme.components.label;
    const computedLabelColor = tryCatch(() => {
      return labelHidden
        ? hideVisually
        : rgba(
            readableColor(theme.base.palette['primary-background']),
            theme.base.transparency['transparent-3']
          );
    });

    const fontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

    return labelHidden
      ? hideVisually
      : css`
          max-width: max-content;
          font-size: ${fontSize[theme.components.label['font-size'] as FontSize]};
          font-weight: ${theme.components.label['font-weight']};
          color: ${labelForegroundColor === 'auto' ? computedLabelColor : labelForegroundColor};
          ${inline &&
          css`
            align-self: center;
          `}
        `;
  }
);

StyledLabel.defaultProps = defaultThemeProp;

const Label: ForwardRefForwardPropsComponent<LabelProps> = forwardRef(function Label(
  { children, labelHidden = false, htmlFor, ...restProps }: PropsWithoutRef<LabelProps>,
  ref: LabelProps['ref']
) {
  return (
    <StyledLabel
      ref={ref as Ref<HTMLLabelElement>}
      labelHidden={labelHidden}
      htmlFor={htmlFor === '' ? undefined : htmlFor}
      {...restProps}
    >
      {children}
    </StyledLabel>
  );
});

export default Label;
